import React, { useState } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { COLUMN_GROUPS, ALL_COLUMN_KEYS, DEFAULT_VIEW_COLUMNS } from './ordersColumns';

const ColumnsPanel = ({
  visibleColumns,
  onChange,
  savedViews,
  activeSavedView,
  isDirty,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onClose,
}) => {
  const [showNameInput, setShowNameInput] = useState(false);
  const [viewName, setViewName] = useState('');

  const toggle = (key) => {
    if (visibleColumns.includes(key)) {
      onChange(visibleColumns.filter((k) => k !== key));
    } else {
      onChange(ALL_COLUMN_KEYS.filter((k) => visibleColumns.includes(k) || k === key));
    }
  };

  const saveNew = () => {
    const name = viewName.trim();
    if (!name) return;
    onSaveView(name);
    setViewName('');
    setShowNameInput(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full sm:w-[463px] max-w-full flex-col bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between px-6 pb-3 pt-5">
          <h2 className="text-[18px] font-semibold tracking-tight text-gray-900">
            Column Visibility
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900"
            aria-label="Close columns panel"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="h-px flex-shrink-0 bg-gray-200" />

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="mb-4 text-[13px] text-gray-500">
            Show or hide columns in the orders table.
          </p>
          <div className="mb-5 flex gap-3">
            <button
              onClick={() => onChange([...DEFAULT_VIEW_COLUMNS])}
              className="h-9 flex-1 rounded-md border border-gray-200 bg-white text-[13px] font-medium hover:bg-gray-50"
            >
              Default
            </button>
            <button
              onClick={() => onChange([...ALL_COLUMN_KEYS])}
              className="h-9 flex-1 rounded-md border border-gray-200 bg-white text-[13px] font-medium hover:bg-gray-50"
            >
              Show All
            </button>
          </div>

          {COLUMN_GROUPS.map((group) => (
            <div key={group.label} className="mb-5">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                {group.label}
              </div>
              {group.columns.map((col) => (
                <label
                  key={col.key}
                  className="flex cursor-pointer items-center gap-3 rounded px-1 py-[7px] text-[13.5px] hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    disabled={col.pinned}
                    checked={col.pinned || visibleColumns.includes(col.key)}
                    onChange={() => toggle(col.key)}
                    className="h-[17px] w-[17px] accent-gray-900 rounded"
                  />
                  <span className={col.pinned ? 'text-gray-500' : 'text-gray-900'}>
                    {col.label}
                  </span>
                  {col.pinned && (
                    <span className="ml-auto rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                      pinned
                    </span>
                  )}
                </label>
              ))}
            </div>
          ))}

          {savedViews.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                Saved Views
              </div>
              {savedViews.map((view) => (
                <div
                  key={view.id}
                  className="group flex items-center justify-between rounded px-1 py-[7px] text-[13.5px] hover:bg-gray-50"
                >
                  <span className="text-gray-900">
                    {view.name}
                    {activeSavedView?.id === view.id && isDirty && (
                      <span className="ml-1 text-gray-500" title="Columns differ from the saved view">
                        &bull;
                      </span>
                    )}
                  </span>
                  <button
                    onClick={() => onDeleteView(view.id)}
                    className="invisible text-gray-400 hover:text-red-600 group-hover:visible"
                    title="Delete view"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4">
          {showNameInput ? (
            <div className="flex gap-2">
              <input
                autoFocus
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNew();
                  if (e.key === 'Escape') setShowNameInput(false);
                }}
                placeholder="View name&hellip;"
                className="h-10 flex-1 rounded-md border border-gray-300 bg-white px-3 text-[13.5px] outline-none placeholder:text-gray-400 focus:ring-1 focus:ring-gray-400"
              />
              <button
                onClick={saveNew}
                disabled={!viewName.trim()}
                className="h-10 rounded-md bg-gray-900 px-4 text-[13.5px] font-medium text-white hover:bg-gray-800 disabled:opacity-40"
              >
                Save
              </button>
            </div>
          ) : isDirty && activeSavedView ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => onUpdateView(activeSavedView.id)}
                className="flex h-10 w-full items-center justify-center rounded-md bg-gray-900 text-[13.5px] font-medium text-white hover:bg-gray-800"
              >
                Update &ldquo;{activeSavedView.name}&rdquo;
              </button>
              <button
                onClick={() => setShowNameInput(true)}
                className="flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white text-[13.5px] font-medium hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" />
                Save as New View
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNameInput(true)}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white text-[13.5px] font-medium hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Save Current View
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ColumnsPanel;
